/**
 * US-E1-002 — Google + Яндекс ID OAuth buttons.
 *
 * Variants (по UX-спеку 2026-05-06, обсуждено AD/UI/UX/Copy):
 *   `circle` (default) — компактные icon-only кнопки 48×48 с tooltip-ами.
 *                       На auth-формах для mobile-first компоновки.
 *   `pill`           — старый «Google» / «Яндекс ID» pill с текстом.
 *                       Для admin-pages / embed-форм где есть много места.
 *
 * Backend GET /api/v1/auth/oauth/{provider}/start returns authorize_url → redirect.
 */
import { authApi } from '@/api/auth'
import { Tooltip } from '@/components/ui/Tooltip'

interface Props {
  variant?: 'circle' | 'pill'
  /** Подпись над иконками. `null` — скрыть (если над OAuth уже есть divider). */
  caption?: string | null
}

export function OAuthButtons({ variant = 'circle', caption = 'или войди через' }: Props) {
  const start = async (provider: 'google' | 'yandex') => {
    try {
      const { authorize_url } = await authApi.oauthStart(provider)
      window.location.href = authorize_url
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('oauth start failed', e)
    }
  }

  if (variant === 'pill') {
    return (
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => start('google')}
          className="flex-1 flex items-center justify-center gap-2.5 px-4 py-3 border border-input-border-default rounded-lg bg-action-secondary-bg hover:bg-action-secondary-bg-hover hover:border-accent-brand-primary text-text-primary font-medium text-sm transition-all focus:outline-none focus:ring-2 focus:ring-border-focus/35"
        >
          <GoogleIcon />
          Google
        </button>
        <button
          type="button"
          onClick={() => start('yandex')}
          className="flex-1 flex items-center justify-center gap-2.5 px-4 py-3 border border-input-border-default rounded-lg bg-action-secondary-bg hover:bg-action-secondary-bg-hover hover:border-accent-brand-primary text-text-primary font-medium text-sm transition-all focus:outline-none focus:ring-2 focus:ring-border-focus/35"
        >
          <YandexIcon />
          Яндекс ID
        </button>
      </div>
    )
  }

  // circle variant
  return (
    <div className="flex flex-col items-center gap-3">
      {caption && (
        <p className="text-xs font-medium uppercase tracking-wider text-text-muted">{caption}</p>
      )}
      <div className="flex items-center gap-3">
        <Tooltip content="Войти через Google" side="bottom">
          <button
            type="button"
            aria-label="Войти через Google"
            onClick={() => start('google')}
            className="w-12 h-12 inline-flex items-center justify-center rounded-full border border-border-strong bg-action-secondary-bg hover:bg-action-secondary-bg-hover hover:border-accent-brand-primary transition-all focus:outline-none focus:ring-2 focus:ring-border-focus/35"
          >
            <GoogleIcon size={24} />
          </button>
        </Tooltip>
        <Tooltip content="Войти через Яндекс ID" side="bottom">
          <button
            type="button"
            aria-label="Войти через Яндекс ID"
            onClick={() => start('yandex')}
            className="w-12 h-12 inline-flex items-center justify-center rounded-full border border-border-strong bg-action-secondary-bg hover:bg-action-secondary-bg-hover hover:border-accent-brand-primary transition-all focus:outline-none focus:ring-2 focus:ring-border-focus/35"
          >
            <YandexIcon size={24} />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}

function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.73.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}
function YandexIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="11" fill="#FC3F1D"/>
      <path fill="#fff" d="M13.32 18.5h2.18V5.5h-3.16c-3.18 0-4.85 1.63-4.85 4.04 0 1.92.92 3.05 2.55 4.21l-2.83 5.18 2.34-.5 2.55-5.36-1.16-.78c-1.32-.89-1.96-1.58-1.96-3.07 0-1.31.92-2.2 2.37-2.2h1.97v11.48z"/>
    </svg>
  )
}
